/* global WEBSOCKET_URL */
import { FC, ReactNode } from 'react';
// import { FC, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
// import { Outlet, useNavigate } from 'react-router-dom';
import { CommunicationsCenter } from '@components/CommunicationsCenter/CommunicationsCenter';
import { AnnouncementCardList } from '@components/CommunicationsCenter/AnnouncementCardList';
import { NotificationCenter } from '@components/interface/Notification';
import { NotificationCenterV2 } from '@components/interface/NotificationCenterV2';
import { ENABLE_ORDERS_V2, ENABLE_AGENT_CHAT_ROLLOUT } from '@utils/featureFlags';
import { StatusBottomBar } from '@components/interface/StatusBottomBar';
import { StatusBottomBarMobile } from '@components/interface/StatusBottomBarMobile';
import { WebBleStatusBadge } from '@components/interface/WebBleStatusBadge';
import { NotificationProvider } from '@context/NotificationsContext';
import { PushNotificationProvider } from '@context/PushNotificationContext';
import { WebSocketProvider } from '@context/WebSocketContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { SubscriptionStatusBar } from '@pages/Subscriptionsv2/components/SubscriptionStatusBar';
import { useInitSubscriptionDetails, useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { ModuleName } from '@utils/constants';
import { useChangeDeliveryStatus } from '@utils/hooks/useChangeDeliveryStatus';
import { useChangeOrderStatus } from '@utils/hooks/useChangeOrderStatus';
import { useChangePaymentStatus } from '@utils/hooks/useChangePaymentStatus';
import { useDesktop } from '@utils/hooks/useDesktop';
import { useIncomingOrder } from '@utils/hooks/useIncomingOrder';
import { useWebBleKeepAlive } from '@utils/hooks/useWebBleKeepAlive';
import { useInitElectronPrinters } from '@utils/hooks/useInitElectronPrinters';
import { useInitElectronVersion } from '@utils/hooks/useInitElectronVersion';
import useModules from '@utils/hooks/useModules';
import usePageView from '@utils/hooks/usePageView';
import { useRiderAssigned } from '@utils/hooks/useRiderAssigned';
import { useSimulateClick } from '@utils/hooks/useSimulateClick';
import NavBar from './NavBar/NavBar';
import Header from './header/Header';
import { MobileTopBarV4 } from '@components/v4/MobileTopBarV4';
import { BusinessClosedBanner } from './BusinessClosedBanner';
// import { Button, Modal } from '@plick/ui';
// import { IconMap2 } from '@tabler/icons-react';
// import { usePostHog } from 'posthog-js/react';
import { NotificationPermissionRequest } from '@components/NotificationPermissionRequest';
import { PwaInstallPrompt } from '@components/PwaInstallPrompt';
import { AgentChatWidget } from '@components/AgentChat/AgentChatWidget';
import { PaulinaProvider } from '@components/AgentChat/PaulinaProvider';
import { PaulinaInviteHost } from '@components/AgentChat/PaulinaInviteHost';
import { PaulinaRolloutOverlays } from '@components/AgentChat/PaulinaRolloutOverlays';
import { useAgentChatGate } from '@hooks/useAgentChatGate';
import { useActiveUserSocket } from '@hooks/useActiveUserSocket';
import { useBranchSessionGuard } from '@utils/hooks/useBranchSessionGuard';
import { useCanSwitchBranch } from '@utils/hooks/useCanSwitchBranch';

const PrivateGlobalHooks = () => {
  usePageView();
  useInitSubscriptionDetails();
  useBranchSessionGuard();
  useIncomingOrder();
  useWebBleKeepAlive();
  useRiderAssigned();
  useChangeOrderStatus();
  useChangePaymentStatus();
  useSimulateClick();
  useInitElectronPrinters();
  useInitElectronVersion();
  useChangeDeliveryStatus();
  return null;
};

/*
 * PaulinaGate — gatea el MONTAJE de Paulina (PaulinaProvider, que arranca polling/nudges/
 * telemetría y expone el contexto que consumen el AgentChatWidget y el avatar del header).
 *
 * Contrato de rollout (default seguro):
 *   - flag OFF = Paulina para todos (estado actual): con ENABLE_AGENT_CHAT_ROLLOUT en false
 *     (build-flag 'agent-chat' AUSENTE, hoy) se monta el PaulinaProvider para TODOS, sin cambio.
 *   - flag ON + backend registra la key 'agent-chat' = rollout por comercio (treatment): el
 *     montaje pasa a depender de useAgentChatGate().offered.
 *
 * El hook se llama SIEMPRE (no puede ir tras un condicional, regla de hooks); este componente
 * es quien decide el render con su resultado. Si Paulina no se monta, `usePaulina()` devuelve
 * null y tanto el AgentChatWidget como el avatar del header (MobileTopBarV4) no renderizan nada.
 */
const PaulinaGate: FC<{ children: ReactNode }> = ({ children }) => {
  const { offered } = useAgentChatGate();
  const mountPaulina = !ENABLE_AGENT_CHAT_ROLLOUT || offered;
  if (!mountPaulina) return <>{children}</>;
  return <PaulinaProvider>{children}</PaulinaProvider>;
};

const Layout: FC = () => {
  const isDesktop = useDesktop();
  useActiveUserSocket();
  const { commerce } = useGetCommerce();
  const { hasModule } = useModules();
  const { isFreemium, isTrial, accountId } = useSubscription();
  // Mismo gate que el silenciado multisucursal (useMultisucursalGate): root, o no-root con permiso.
  const { hasPermission: canSeeOtherBranches } = useCanSwitchBranch();
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const navigate = useNavigate();
  // const posthog = usePostHog();

  // ========================================
  // MODAL BLOQUEANTE DE DIRECCIÓN - DESACTIVADO
  // ========================================
  // Este modal se desactivó porque ahora tenemos la página de /configuracion-inicial
  // que maneja la configuración de dirección de forma más amigable y no bloqueante
  // useEffect(() => {
  //   if (!commerce.id) return;

  //   if (!commerce?.address?.latitude || !commerce?.address?.longitude) {
  //     setIsModalOpen(true);
  //   }
  // }, [commerce]);

  return (
    <WebSocketProvider
      url={WEBSOCKET_URL}
      accountId={accountId}
      commerceId={commerce?.id}
      canSeeOtherBranches={canSeeOtherBranches}
      allowConnection={hasModule(ModuleName.HISTORIAL) || false}
    >
      <NotificationProvider>
        <PushNotificationProvider>
          <PrivateGlobalHooks />
          {/* PaulinaGate gatea el MONTAJE de Paulina (PaulinaProvider, que comparte UN solo
              estado entre el avatar del header móvil y el host del chat AgentChatWidget —
              subárboles hermanos). flag OFF = Paulina para todos (estado actual); flag ON +
              backend registra la key 'agent-chat' = rollout por comercio (treatment). Dentro
              de NotificationProvider para que la señal de cesión (usePaulinaShouldYield) tenga
              acceso a las notificaciones. */}
          <PaulinaGate>
            <main className="w-screen h-screen flex flex-col">
              {isDesktop ? <Header /> : <MobileTopBarV4 />}
              <div className="flex flex-1 flex-col overflow-hidden pt-[52px] lg:pt-14 pb-[62.14px] lg:pb-0">
                <BusinessClosedBanner />
                <div className="flex flex-1 flex-col-reverse lg:flex-row overflow-hidden min-h-0">
                  <NavBar />

                  {(isFreemium || isTrial) && !isDesktop && <SubscriptionStatusBar isMobile />}
                  {/* StatusBottomBar Mobile: versión colapsable top */}
                  {!isDesktop && <StatusBottomBarMobile />}
                  <div className="relative flex-1 flex flex-col min-w-0 overflow-auto">
                    <WebBleStatusBadge />
                    <AnnouncementCardList />
                    <Outlet />
                  </div>
                </div>
              </div>
              {/* StatusBottomBar Desktop: versión tradicional bottom */}
              {isDesktop && <StatusBottomBar />}
              {(isFreemium || isTrial) && isDesktop && <SubscriptionStatusBar />}
              {/* ========================================
              MODAL BLOQUEANTE DE DIRECCIÓN - DESACTIVADO
              ========================================
              Este modal se desactivó porque ahora tenemos la página de /configuracion-inicial
              que maneja la configuración de dirección de forma más amigable y no bloqueante
          */}
              {/* <Modal isOpen={isModalOpen}>
            <div className="flex flex-col items-center justify-center gap-4 p-4">
              <div className="flex items-center justify-center">
                <div
                  className="rounded-full p-3 border-[0.375rem] flex items-center justify-center"
                  style={{ borderColor: '#253746' }}
                >
                  <div className="w-[3.75rem] h-[3.75rem] flex items-center justify-center">
                    <IconMap2 className="w-full h-full" />
                  </div>
                </div>
              </div>
              <h1 className="text-xl text-center px-6 font-bold">
                Aparece en el catálogo de comercios de tu ciudad, sin costo.
              </h1>
              <h2 className="px-0 text-center mb-8">
                No dejes pasar la oportunidad de ser más visible y aumentar tus ventas.
              </h2>
              <h4 className="text-center">Solo te tomará 1 minuto. Da clic aquí 👇 y date de alta</h4>
              <Button.Primary
                text="Actualizar mi dirección"
                onClick={() => {
                  posthog.capture('update_commerce_location_click');
                  setIsModalOpen(false);
                  navigate('/datos-del-comercio/ubicacion');
                }}
              />
            </div>
          </Modal> */}
            </main>
            {ENABLE_ORDERS_V2 ? <NotificationCenterV2 /> : <NotificationCenter />}
            <CommunicationsCenter />
            <NotificationPermissionRequest />
            <PwaInstallPrompt />
            <AgentChatWidget />
          </PaulinaGate>
          {/* Superficies del rollout de Paulina que viven FUERA de PaulinaGate: la invitación
              ("Conoce a Paulina") se muestra en control (provider sin montar) y el prompt de
              feedback al desactivar sobrevive al revert (que desmonta el provider). Solo con el
              flag de rollout ON; con el flag OFF (estado actual) el gate es inerte y no aplican. */}
          {ENABLE_AGENT_CHAT_ROLLOUT && (
            <>
              <PaulinaInviteHost />
              <PaulinaRolloutOverlays />
            </>
          )}
        </PushNotificationProvider>
      </NotificationProvider>
    </WebSocketProvider>
  );
};

export default Layout;
